import { useContext, useEffect, useRef, type FC } from "react";
import { useLocation } from "react-router-dom";
import {
  usePlatformConfig,
  usePlatformConfigHost
} from "@kivunova/kivufrontendcommon";
import AuthContext from "../context/authContext";
import { AUTH_STATUS } from "../context/enums/authEnums";
import { isAuthenticatedAppRoute } from "../Utils/authenticatedAppRoute";
import { useLoadBusinessTypeConfig } from "../hooks/api/useLoadBusinessTypeConfig";

const catalogFingerprint = (
  businessType: string,
  sellingMode: string[],
  baseUnit: string[]
): string => `${businessType}|${sellingMode.join(",")}|${baseUnit.join(",")}`;

/** Tenant enum catalog — fetch on portal/membership after authorize (inside AuthProvider). */
const HostBusinessTypeConfigSync: FC = () => {
  const location = useLocation();
  const { authContextInfo } = useContext(AuthContext);
  const { config } = usePlatformConfig();
  const {
    setPlatformConfig,
    setPlatformConfigLoadError,
    setPlatformConfigLoading
  } = usePlatformConfigHost();
  const loadCatalog = useLoadBusinessTypeConfig();
  const hasAttemptedCatalog = useRef(false);
  const appliedCatalogKeyRef = useRef<string | null>(null);

  const loginSucceeded =
    authContextInfo.auth.status === AUTH_STATUS.AUTHENTICATED &&
    authContextInfo.auth.authorized === true;
  const canFetchCatalog =
    loginSucceeded && isAuthenticatedAppRoute(location.pathname);

  useEffect(() => {
    if (!canFetchCatalog) {
      hasAttemptedCatalog.current = false;
      loadCatalog.reset();
      return;
    }
    if (
      loadCatalog.isSuccess ||
      loadCatalog.isPending ||
      loadCatalog.isError ||
      hasAttemptedCatalog.current
    ) {
      return;
    }

    hasAttemptedCatalog.current = true;
    loadCatalog.mutate();
  }, [
    canFetchCatalog,
    loadCatalog.isSuccess,
    loadCatalog.isPending,
    loadCatalog.isError,
    loadCatalog.mutate
  ]);

  useEffect(() => {
    if (!canFetchCatalog) {
      return;
    }
    setPlatformConfigLoading(loadCatalog.isPending);
  }, [canFetchCatalog, loadCatalog.isPending, setPlatformConfigLoading]);

  useEffect(() => {
    if (!canFetchCatalog) {
      const hasTenantCatalog =
        config?.businessType != null ||
        (config?.sellingMode?.length ?? 0) > 0 ||
        (config?.baseUnit?.length ?? 0) > 0;

      if (hasTenantCatalog && appliedCatalogKeyRef.current != null) {
        appliedCatalogKeyRef.current = null;
        setPlatformConfig({
          stage: config?.stage ?? null,
          businessTypes: config?.businessTypes ?? [],
          featureFlags: config?.featureFlags ?? {},
          businessType: null,
          sellingMode: [],
          baseUnit: []
        });
      }

      if (loadCatalog.isError) {
        setPlatformConfigLoadError(false);
      }
      return;
    }

    if (loadCatalog.isError) {
      setPlatformConfigLoadError(true);
      return;
    }

    if (!loadCatalog.data) {
      return;
    }

    const nextKey = catalogFingerprint(
      loadCatalog.data.businessType,
      loadCatalog.data.sellingMode,
      loadCatalog.data.baseUnit
    );
    if (appliedCatalogKeyRef.current === nextKey) {
      return;
    }
    appliedCatalogKeyRef.current = nextKey;

    setPlatformConfig({
      stage: config?.stage ?? null,
      businessTypes: config?.businessTypes ?? [],
      featureFlags: config?.featureFlags ?? {},
      businessType: loadCatalog.data.businessType,
      sellingMode: loadCatalog.data.sellingMode,
      baseUnit: loadCatalog.data.baseUnit
    });
    setPlatformConfigLoadError(false);
  }, [
    canFetchCatalog,
    config?.stage,
    config?.businessTypes,
    config?.featureFlags,
    config?.businessType,
    config?.sellingMode,
    config?.baseUnit,
    loadCatalog.data,
    loadCatalog.isError,
    setPlatformConfig,
    setPlatformConfigLoadError
  ]);

  return null;
};

export default HostBusinessTypeConfigSync;
